import { useState, useEffect } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import { Shield, Fingerprint, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BIOMETRIC_KEY = 'bk_biometric_credential';

// Convert ArrayBuffer ⇄ base64url (used to (de)serialize WebAuthn IDs)
const bufToB64u = (buf) => {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const b64uToBuf = (s) => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const webauthnSupported = () =>
  typeof window !== 'undefined' &&
  !!window.PublicKeyCredential &&
  typeof navigator?.credentials?.create === 'function';

export default function Login({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [enrolledLabel, setEnrolledLabel] = useState(null);

  // Detect existing enrollment on this device
  useEffect(() => {
    try {
      const raw = localStorage.getItem(BIOMETRIC_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setEnrolledLabel(parsed?.email || 'this device');
      }
    } catch { /* ignore */ }
  }, []);

  const finishLogin = (token) => {
    onLogin(token);
    toast.success('Login successful');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const { data } = await axios.post(endpoint, formData);

      // Offer fingerprint enrollment if device supports it and not yet enrolled
      const alreadyEnrolled = !!localStorage.getItem(BIOMETRIC_KEY);
      if (webauthnSupported() && !alreadyEnrolled) {
        setPendingToken(data.token);
        setShowEnroll(true);
        toast.success(isRegister ? 'Registration successful' : 'Login successful');
      } else {
        finishLogin(data.token);
        if (isRegister) toast.success('Registration successful');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Fingerprint LOGIN (returning user) ----------
  const handleFingerprintLogin = async () => {
    if (scanning) return;
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem(BIOMETRIC_KEY) || 'null');
    } catch { /* ignore */ }

    if (!stored?.credential_id) {
      toast.error('No fingerprint enrolled on this device. Sign in with password first to enroll.');
      return;
    }

    setScanning(true);
    try {
      // 1) Trigger the platform authenticator (Touch ID / Windows Hello / Android fingerprint)
      if (webauthnSupported()) {
        const challenge = crypto.getRandomValues(new Uint8Array(32));
        await navigator.credentials.get({
          publicKey: {
            challenge,
            timeout: 30000,
            userVerification: 'required',
            allowCredentials: [{ id: b64uToBuf(stored.credential_id), type: 'public-key' }],
          },
        });
      } else {
        // Demo fallback (no WebAuthn): show 1.2s scan animation
        await new Promise((r) => setTimeout(r, 1200));
      }

      // 2) Exchange credential_id for a JWT
      const { data } = await axios.post('/auth/biometric/login', {
        credential_id: stored.credential_id,
      });
      finishLogin(data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Fingerprint authentication failed';
      // If user cancelled biometric prompt, NotAllowedError; if no enrollment server-side, 401
      if (err?.name === 'NotAllowedError') {
        toast.error('Biometric prompt cancelled');
      } else if (err?.response?.status === 401) {
        toast.error('Fingerprint not recognised — please re-enroll');
        localStorage.removeItem(BIOMETRIC_KEY);
        setEnrolledLabel(null);
      } else {
        toast.error(msg);
      }
    } finally {
      setScanning(false);
    }
  };

  // ---------- Fingerprint ENROLL (after first password login) ----------
  const handleEnrollFingerprint = async () => {
    if (!pendingToken) return;
    setScanning(true);
    try {
      let credentialIdB64;

      if (webauthnSupported()) {
        const challenge = crypto.getRandomValues(new Uint8Array(32));
        const userId = crypto.getRandomValues(new Uint8Array(16));
        const cred = await navigator.credentials.create({
          publicKey: {
            challenge,
            rp: { name: 'MCI - BRAHM KAVACH' },
            user: {
              id: userId,
              name: formData.email,
              displayName: formData.email,
            },
            pubKeyCredParams: [
              { type: 'public-key', alg: -7 },   // ES256
              { type: 'public-key', alg: -257 }, // RS256
            ],
            authenticatorSelection: {
              authenticatorAttachment: 'platform',
              userVerification: 'required',
              residentKey: 'preferred',
            },
            timeout: 60000,
            attestation: 'none',
          },
        });
        credentialIdB64 = bufToB64u(cred.rawId);
      } else {
        // Demo fallback — generate a stable fake credential id
        credentialIdB64 = 'demo-' + bufToB64u(crypto.getRandomValues(new Uint8Array(24)));
      }

      // Persist server-side
      await axios.post(
        '/auth/biometric/enroll',
        { credential_id: credentialIdB64, label: 'Fingerprint' },
        { headers: { Authorization: `Bearer ${pendingToken}` } }
      );

      // And on this device for fast future logins
      localStorage.setItem(
        BIOMETRIC_KEY,
        JSON.stringify({ credential_id: credentialIdB64, email: formData.email })
      );
      setEnrolledLabel(formData.email);
      toast.success('Fingerprint enrolled — sign in with biometrics next time');
      finishLogin(pendingToken);
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        toast.error('Enrollment cancelled');
      } else {
        toast.error(err?.response?.data?.detail || err?.message || 'Enrollment failed');
      }
      // On failure, still let them in with the password token
      if (pendingToken) finishLogin(pendingToken);
    } finally {
      setScanning(false);
      setShowEnroll(false);
      setPendingToken(null);
    }
  };

  const skipEnroll = () => {
    if (pendingToken) finishLogin(pendingToken);
    setShowEnroll(false);
    setPendingToken(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#27272a_1px,transparent_1px),linear-gradient(to_bottom,#27272a_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20"></div>

      <div className="relative z-10 w-full max-w-md p-8">
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-sm p-8 backdrop-blur-sm corner-accent">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-sky-500/10 border border-sky-500/30 rounded-sm flex items-center justify-center mb-4">
              <Shield size={32} className="text-sky-500" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight uppercase text-center">
              MCI - BRAHM KAVACH<sup className="text-sm ml-0.5">TM</sup>
            </h1>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-2 font-mono text-center">
              Multi-Domain Counter-Drone Detection & Mitigation System
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-xs font-mono tracking-wider text-zinc-500 uppercase">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-zinc-900/50 border-zinc-800 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 rounded-none font-mono text-sm"
                  required={isRegister}
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-xs font-mono tracking-wider text-zinc-500 uppercase">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 bg-zinc-900/50 border-zinc-800 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 rounded-none font-mono text-sm"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs font-mono tracking-wider text-zinc-500 uppercase">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2 bg-zinc-900/50 border-zinc-800 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 rounded-none font-mono text-sm"
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-sky-600 hover:bg-sky-500 text-white rounded-none uppercase tracking-wider font-bold text-xs px-4 py-3 border border-sky-400/20 hover:border-sky-400"
              data-testid="submit-button"
            >
              {loading ? 'PROCESSING...' : isRegister ? 'REGISTER' : 'LOGIN'}
            </Button>
          </form>

          {/* Divider + Fingerprint option */}
          <div className="mt-6">
            <div className="flex items-center gap-3 text-[10px] font-mono tracking-widest text-zinc-600 uppercase mb-4">
              <span className="flex-1 h-px bg-zinc-800" />
              <span>or</span>
              <span className="flex-1 h-px bg-zinc-800" />
            </div>
            <button
              type="button"
              onClick={handleFingerprintLogin}
              disabled={scanning}
              data-testid="fingerprint-login-button"
              className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-none border uppercase tracking-wider font-bold text-xs transition-all
                ${scanning
                  ? 'bg-emerald-500/10 border-emerald-400 text-emerald-300 cursor-wait'
                  : 'bg-zinc-900 hover:bg-zinc-800 border-zinc-700 hover:border-emerald-500/60 text-zinc-300 hover:text-emerald-300'}`}
            >
              <span className="relative flex items-center justify-center">
                <Fingerprint size={20} className={scanning ? 'animate-pulse' : ''} />
                {scanning && (
                  <span className="absolute inset-[-6px] rounded-full border border-emerald-400/40 animate-ping" />
                )}
              </span>
              {scanning ? 'SCANNING…' : enrolledLabel ? 'SIGN IN WITH FINGERPRINT' : 'SIGN IN WITH FINGERPRINT'}
            </button>
            {enrolledLabel ? (
              <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider text-center mt-2" data-testid="biometric-status">
                Enrolled · {enrolledLabel}
              </p>
            ) : (
              <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider text-center mt-2">
                Sign in with password first to enroll fingerprint
              </p>
            )}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-xs text-zinc-500 hover:text-sky-500 uppercase tracking-wider font-mono"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? 'Already have access? Login' : 'Need access? Register'}
            </button>
          </div>
        </div>
      </div>

      {/* Fingerprint enrollment modal */}
      {showEnroll && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          data-testid="biometric-enroll-modal"
        >
          <div className="bg-zinc-950 border border-zinc-700 rounded-sm p-8 max-w-sm w-full mx-4 corner-accent relative">
            <button
              onClick={skipEnroll}
              className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-200"
              aria-label="Skip enrollment"
            >
              <X size={16} />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center mb-4 relative">
                <Fingerprint size={42} className={`text-emerald-400 ${scanning ? 'animate-pulse' : ''}`} />
                {scanning && (
                  <>
                    <span className="absolute inset-[-4px] rounded-full border border-emerald-400/50 animate-ping" />
                    <span className="absolute inset-[-12px] rounded-full border border-emerald-400/20 animate-pulse" />
                  </>
                )}
              </div>
              <h2 className="text-base font-bold uppercase tracking-wider text-zinc-100 mb-1">Enable Fingerprint Login</h2>
              <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-6">
                Authenticate faster next time using your device's biometric scanner
              </p>
              <div className="flex gap-2 w-full">
                <button
                  onClick={skipEnroll}
                  disabled={scanning}
                  data-testid="biometric-enroll-skip"
                  className="flex-1 px-4 py-2.5 border border-zinc-700 hover:border-zinc-500 rounded-none uppercase font-mono text-[11px] tracking-wider text-zinc-400 hover:text-zinc-200"
                >
                  Skip
                </button>
                <button
                  onClick={handleEnrollFingerprint}
                  disabled={scanning}
                  data-testid="biometric-enroll-confirm"
                  className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 rounded-none uppercase font-mono text-[11px] tracking-wider text-white border border-emerald-400/40"
                >
                  {scanning ? 'Scanning…' : 'Enable'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
